// TelcoTrack — /users a /users/firmy jsou dvě záložky. Bez uloženého profilu jen vlastní profil a Kontakty.

import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import {
  canOpenCompanyList,
  hasPermission,
  loadedPermissionProfile,
  type UserPermissionProfile,
} from '@core/shared-bridge';

function normalizeEmail(s: string | undefined | null): string {
  return String(s ?? '')
    .trim()
    .toLowerCase();
}

function firstSegmentUserIdFromUrl(url: string): string | null {
  const m = /^\/users\/([^/?#]+)/i.exec(url);
  return m?.[1] ? decodeURIComponent(m[1]).trim() : null;
}

function ownProfileTree(router: Router, email: string): UrlTree {
  return router.parseUrl(`/users/${encodeURIComponent(email)}/profil`);
}

function isPeopleCalendarSegment(segment: string | null): boolean {
  return segment === 'kalendar' || segment === 'kontroly';
}

function isContactsSegment(segment: string | null): boolean {
  return segment === 'kontakty';
}

/** Terénní účet bez adresáře: cizí id, Firmy i kalendář vedou na vlastní profil; Kontakty jsou veřejné v rámci organizace/aplikace. */
function fieldUserTree(router: Router, user: { email?: string } | null, url: string): boolean | UrlTree {
  const segment = firstSegmentUserIdFromUrl(url);
  if (isContactsSegment(segment)) return true;
  const myEmail = normalizeEmail(user?.email);
  if (!myEmail) return router.parseUrl('/dashboard');
  const ownProfile = ownProfileTree(router, myEmail);
  if (!segment) return ownProfile;
  if (isPeopleCalendarSegment(segment) || segment === 'firmy' || segment === 'organizace' || normalizeEmail(segment) !== myEmail) {
    return ownProfile;
  }
  return true;
}

function profileTree(
  router: Router,
  user: { email?: string } | null,
  url: string,
  profile: UserPermissionProfile,
): boolean | UrlTree {
  const path = url.split(/[?#]/)[0] ?? '';
  const segment = firstSegmentUserIdFromUrl(url);
  if (isContactsSegment(segment)) return true;
  const companyList = canOpenCompanyList(profile);
  const directory = hasPermission(profile, 'users', 'view') || companyList;
  if (!directory) return fieldUserTree(router, user, url);
  const companiesPath = segment === 'firmy' || segment === 'organizace' || path.startsWith('/users/firmy/');
  if (!companyList && companiesPath) return router.parseUrl('/users');
  if (isPeopleCalendarSegment(segment) && !hasPermission(profile, 'users-checks', 'view')) {
    return router.parseUrl('/users');
  }
  return true;
}

export const usersDirectoryGuard: CanActivateFn = (_route, state): boolean | UrlTree => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const user = auth.getCurrentUser();
  const profile = loadedPermissionProfile(user?.permissionProfile);
  if (profile) return profileTree(router, user, state.url, profile);
  return fieldUserTree(router, user, state.url);
};
